import type { DockingRecord } from '../types/docking';

const STORAGE_KEY = 'zero-carbon-park:docking-records';
const MOCK_JSON = `${process.env.PUBLIC_URL ?? ''}/data/dockingRecords.json`;

let seedLoaded = false;

function readFromStorage(): DockingRecord[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as DockingRecord[];
  } catch { /* ignore */ }
  return [];
}

function writeToStorage(records: DockingRecord[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(records));
}

async function loadSeedData(): Promise<DockingRecord[]> {
  const res = await fetch(MOCK_JSON, { cache: 'no-cache' });
  if (!res.ok) return [];
  const data = await res.json();
  if (!Array.isArray(data)) return [];
  return data as DockingRecord[];
}

/**
 * Always load seed JSON and reconcile with localStorage.
 * - New seed records are appended.
 * - Seed records removed from JSON are purged from localStorage.
 * - Seed records updated in JSON overwrite the localStorage copy.
 * - User-created records (id not starting with "dk-0") are preserved.
 */
export async function fetchDockingRecords(): Promise<DockingRecord[]> {
  let stored = readFromStorage();
  if (!seedLoaded) {
    const seed = await loadSeedData();
    const seedIds = new Set(seed.map((r) => r.id));
    const seedMap = new Map(seed.map((r) => [r.id, r]));

    const isSeedId = (id: string) => /^dk-\d{3}$/.test(id);

    // Remove stale seed records & update existing seed records
    stored = stored.filter((r) => {
      if (isSeedId(r.id) && !seedIds.has(r.id)) return false;
      return true;
    });
    stored = stored.map((r) => {
      const updated = seedMap.get(r.id);
      if (updated) return updated;
      return r;
    });

    // Append new seed records
    const existingIds = new Set(stored.map((r) => r.id));
    for (const s of seed) {
      if (!existingIds.has(s.id)) {
        stored.push(s);
      }
    }

    writeToStorage(stored);
    seedLoaded = true;
  }
  return stored.filter((r) => !r.archived);
}

export function getAllDockingRecords(): DockingRecord[] {
  return readFromStorage().filter((r) => !r.archived);
}

export function saveDockingRecord(record: DockingRecord): void {
  const records = readFromStorage();
  const idx = records.findIndex((r) => r.id === record.id);
  if (idx >= 0) {
    records[idx] = { ...record, updatedAt: new Date().toISOString() };
  } else {
    records.push(record);
  }
  writeToStorage(records);
}

export function deleteDockingRecord(id: string): void {
  const records = readFromStorage();
  const idx = records.findIndex((r) => r.id === id);
  if (idx >= 0) {
    records[idx] = { ...records[idx], archived: true, updatedAt: new Date().toISOString() };
    writeToStorage(records);
  }
}

export function generateRecordId(): string {
  return `dk-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function generateTaskId(): string {
  return `t-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}
